import { cva, type VariantProps } from "./style.utils";
import { textInputVariants } from "./text-input.styles";

// -----------------------------------------------------------------------------

export type PhoneInputRootVariants = VariantProps<
  typeof phoneInputRootVariants
>;

export const phoneInputRootVariants = cva({
  base: [
    // layout
    "inline-flex w-full min-w-120px items-center",
    // radii
    "rounded-md",
    // border
    "border",
    // focus-within
    "focus-within:outline focus-within:outline-2 focus-within:outline-ring-normal",
  ],
  variants: {
    invalid: {
      true: "",
      false: "",
    },
    disabled: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      invalid: false,
      disabled: false,
      className: [
        // border
        "border-shade-50",
        // background
        "bg-white",
        // focus-within
        "focus-within:-outline-offset-2",
      ],
    },
    {
      invalid: true,
      disabled: false,
      className: [
        // ring
        "ring-1 ring-error-100",
        // border
        "border-error-100",
        // background
        "bg-error-20",
        // focus-within
        "focus-within:-outline-offset-1",
        // focus-within ring
        "focus-within:ring-ring-normal",
      ],
    },
    {
      invalid: [true, false],
      disabled: true,
      className: [
        // disabled border
        "border-shade-10",
        // disabled background
        "bg-shade-2",
      ],
    },
  ],
  defaultVariants: {
    invalid: false,
    disabled: false,
  },
});

// -----------------------------------------------------------------------------

export type PhoneInputTriggerVariants = VariantProps<
  typeof phoneInputTriggerVariants
>;

export const phoneInputTriggerVariants = cva({
  base: [
    // layout
    "inline-flex items-center justify-center",
    // radii
    "rounded-md",
    // foreground
    "text-sm font-semibold",
    // animation
    "transition-colors",
    // focus-visible
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring-normal",
    // disabled cursor
    "disabled:cursor-not-allowed",
  ],
  variants: {
    size: {
      sm: "h-full w-30px *:w-12px", // compact
      md: "h-full w-36px *:w-16px", // convenient
      lg: "h-full w-40px *:w-18px", // cozy
    },
    active: {
      true: "",
      false: "",
    },
    invalid: {
      true: "",
      false: "",
    },
    disabled: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      invalid: false,
      disabled: false,
      className: [
        // foreground
        "text-shade-100",
        // focus-within
        "focus-visible:-outline-offset-1",
      ],
    },
    {
      invalid: true,
      disabled: false,
      className: [
        // foreground
        "text-error-120",
        // focus-within
        "focus-visible:outline-offset-0",
      ],
    },
    {
      invalid: [true, false],
      disabled: true,
      className: "text-shade-100/20",
    },
  ],
  defaultVariants: {
    size: "md",
    active: false,
    invalid: false,
    disabled: false,
  },
});

// -----------------------------------------------------------------------------

export type PhoneInputControlVariants = VariantProps<
  typeof phoneInputControlVariants
>;

export const phoneInputControlVariants = cva({
  composes: [textInputVariants],
  base: [
    // reset variants
    "!border-0 !outline-0 !ring-0",
    // layout
    "min-w-0 flex-1 pl-0",
    // foreground mobile/zoom fix
    "sm:text-sm",
  ],
  variants: {
    size: {
      // 2px less to accomodate root borders
      sm: "h-28px", // compact
      md: "h-34px", // convenient
      lg: "h-38px", // cozy
    },
    invalid: {
      true: "",
      false: "",
    },
    disabled: {
      true: "",
      false: "",
    },
  },
  defaultVariants: {
    size: "md",
    invalid: false,
    disabled: false,
  },
});
